import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Check, ChevronRight, ArrowLeft, Search, RotateCw, X, Plus, Undo2, Phone, Truck, PackageOpen, FileDown,
} from 'lucide-react';
import { cldThumb } from '../utils/img';
import { downloadHtml } from '../utils/download';
import { generateReceipt } from '../api/receipts';
import {
  listCourierOrders, setCourierItems, requestAppPayment, markCourierPaid, markCourierRto, lookupCourierPiece,
} from '../api/catalogCourier';

// Order Ops → Catalog — no rider.
//
// A courier order is worked in three steps, top to bottom on one card:
//   1. Bill      what the customer keeps (starts from what they booked; add or remove pieces)
//   2. Payment   in the Skipzy app (Scan & Pay becomes Pay now) or direct UPI with the UTR
//   3. Invoice   once paid
// The bill records the sale only: the box is not changed. A booked piece the customer does not
// keep is listed under "Coming back" and is reversed when the parcel returns. RTO is a separate
// action at the foot of the card.

// Skipzy's own UPI (decoded from the ICICI eazypay QR riders show customers). The QR is drawn here
// with the order amount added (am=), so the customer's UPI app opens with the amount filled in.
const upiLink = (amount) => 'upi://pay?pa=MSZODDLELIFESTYLEPRIVATELIMITED1.eazypay@icici'
  + '&pn=MS.ZODDLE%20LIFESTYLE%20PRIVATE%20LIMITED&tr=EZYS9873030712&cu=INR&mc=NULL'
  + (amount > 0 ? `&am=${Number(amount).toFixed(2)}` : '');

const rupees = (v) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : (d?.message || fallback);
};
const when = (iso) => (iso ? new Date(`${iso}Z`).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
// "12m ago" / "5h ago" / "Yesterday" / "3d ago" for scanning; the exact time sits underneath.
const ago = (iso) => {
  if (!iso) return '—';
  const mins = Math.max(0, Math.round((Date.now() - new Date(`${iso}Z`).getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? 'Yesterday' : `${days}d ago`;
};
const initials = (name) => (name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
const AVATAR_TINTS = ['bg-sky-100 text-sky-800', 'bg-violet-100 text-violet-800', 'bg-amber-100 text-amber-900', 'bg-emerald-100 text-emerald-800', 'bg-rose-100 text-rose-800', 'bg-teal-100 text-teal-800'];
const tintFor = (s) => AVATAR_TINTS[[...(s || '')].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_TINTS.length];

// Shared column layout for the list header and each closed row (sm and up).
const ROW_GRID = 'sm:grid sm:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,0.9fr)_9rem] sm:items-center sm:gap-4';

// Shared control styles: one focus ring and one press response for every control on the tab.
const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-zoddle-green/60 focus-visible:ring-offset-1';
const PRESS = 'transition-transform duration-150 ease-out active:scale-[0.97] disabled:active:scale-100';
const INPUT = `border border-gray-300 rounded-lg px-3 py-2 text-sm placeholder:text-gray-500 ${FOCUS} focus-visible:border-zoddle-green`;
const BTN_PRIMARY = `inline-flex items-center gap-1.5 bg-zoddle-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed ${PRESS} ${FOCUS}`;
const BTN_SECONDARY = `inline-flex items-center gap-1.5 bg-white text-gray-700 text-sm font-medium border border-gray-300 px-3.5 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed ${PRESS} ${FOCUS}`;

// Where the order is: drives the status chip and the list tabs.
function stageOf(order) {
  if (order?.payment_status === 'PAID') return 'paid';
  if (order?.payment_status === 'PENDING') return 'awaiting';
  if (order) return 'billed';
  return 'new';
}
const STAGES = {
  new:      { label: 'Needs bill',       chip: 'bg-gray-100 text-gray-700',          dot: 'bg-gray-400' },
  billed:   { label: 'Bill ready',       chip: 'bg-amber-50 text-amber-800',         dot: 'bg-amber-500' },
  awaiting: { label: 'Awaiting payment', chip: 'bg-sky-50 text-sky-800',             dot: 'bg-zoddle-blue' },
  paid:     { label: 'Paid',             chip: 'bg-green-50 text-green-800',         dot: 'bg-zoddle-green' },
};

function StatusChip({ stage }) {
  const st = STAGES[stage];
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${st.chip}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} aria-hidden="true" />
      {st.label}
    </span>
  );
}

function Thumb({ url, size = 'w-12 h-12' }) {
  return url
    ? <img src={cldThumb(url, 300)} alt="" className={`${size} shrink-0 object-cover rounded-md ring-1 ring-gray-200`} />
    : <div className={`${size} shrink-0 rounded-md bg-gray-100 ring-1 ring-gray-200`} />;
}

function Tag({ tone = 'gray', children }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-700',
    green: 'bg-green-50 text-green-800',
    amber: 'bg-amber-50 text-amber-800',
  };
  return <span className={`inline-flex text-[11px] font-medium px-1.5 py-0.5 rounded ${tones[tone]}`}>{children}</span>;
}

// Result of an action, shown inside the step where it was taken.
function Notice({ msg }) {
  if (!msg) return null;
  return (
    <p role="status" className={`text-sm font-medium ${msg.kind === 'ok' ? 'text-green-700' : 'text-zoddle-pink'}`}>
      {msg.text}
    </p>
  );
}

// One step on the card's vertical rail. state: 'done' | 'current' | 'upcoming'.
function Step({ n, title, state, meta, last, children }) {
  const marker = {
    done: 'bg-zoddle-green text-white',
    current: 'bg-white text-green-700 ring-2 ring-zoddle-green',
    upcoming: 'bg-gray-100 text-gray-500',
  }[state];
  return (
    <li className="relative flex gap-4">
      {!last && <span className="absolute left-3 top-7 bottom-0 w-px bg-gray-200" aria-hidden="true" />}
      <span className={`relative z-10 mt-0.5 w-6 h-6 shrink-0 rounded-full text-xs font-bold flex items-center justify-center ${marker}`}>
        {state === 'done' ? <Check size={14} strokeWidth={3} aria-label="Done" /> : n}
      </span>
      <div className={`min-w-0 flex-1 ${last ? '' : 'pb-6'}`}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-h-[28px]">
          <h4 className={`text-sm font-semibold ${state === 'upcoming' ? 'text-gray-500' : 'text-gray-900'}`}>{title}</h4>
          {meta && <div className="ml-auto text-xs">{meta}</div>}
        </div>
        <div className="mt-2">{children}</div>
      </div>
    </li>
  );
}

function BillSkeleton() {
  return (
    <div className="space-y-3 animate-pulse motion-reduce:animate-none" aria-label="Loading pieces">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-md bg-gray-100" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-1/3 rounded bg-gray-100" />
            <div className="h-2.5 w-1/5 rounded bg-gray-100" />
          </div>
          <div className="h-3 w-16 rounded bg-gray-100" />
        </div>
      ))}
    </div>
  );
}

// ── Step 1: the bill ────────────────────────────────────────────────────────
function Bill({ row, locked, onDirty, onSaved }) {
  const order = row.order;
  const [lines, setLines] = useState(null);   // barcode -> piece with { inBox, booked, buying }
  const [saved, setSaved] = useState('');     // serialised set of billed barcodes, to detect edits
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      const inBox = new Set(row.in_box.map((p) => p.zoddle_barcode));
      const booked = new Set(row.booked.map((b) => b.zoddle_barcode));
      const billed = new Set((order?.items || []).map((p) => p.zoddle_barcode));
      const all = Array.from(new Set([...booked, ...inBox, ...billed]));
      const out = {};
      await Promise.all(all.map(async (bc) => {
        const known = row.in_box.find((p) => p.zoddle_barcode === bc) || (order?.items || []).find((p) => p.zoddle_barcode === bc);
        let piece = known;
        if (!piece) { try { piece = await lookupCourierPiece(bc); } catch (e) { piece = { zoddle_barcode: bc }; } }
        out[bc] = { ...piece, inBox: inBox.has(bc), booked: booked.has(bc), buying: order ? billed.has(bc) : booked.has(bc) };
      }));
      if (!live) return;
      setLines(out);
      setSaved(JSON.stringify(Object.keys(out).filter((k) => out[k].buying).sort()));
    })();
    return () => { live = false; };
  }, [row, order]);

  const all = useMemo(() => Object.values(lines || {}), [lines]);
  const buying = all.filter((p) => p.buying);
  const coming = all.filter((p) => !p.buying);
  const total = buying.reduce((s, p) => s + Number(p.price || 0), 0);
  const dirty = !!lines && JSON.stringify(buying.map((p) => p.zoddle_barcode).sort()) !== saved;
  useEffect(() => { onDirty(dirty && !!order); }, [dirty, order, onDirty]);

  const toggle = (bc) => setLines((l) => ({ ...l, [bc]: { ...l[bc], buying: !l[bc].buying } }));

  const add = async () => {
    const bc = code.trim();
    if (!bc) return;
    setErr(null);
    if (lines[bc]) { setLines((l) => ({ ...l, [bc]: { ...l[bc], buying: true } })); setCode(''); return; }
    setBusy('add');
    try {
      const piece = await lookupCourierPiece(bc);
      if (piece.sold) { setErr(`${bc} is already sold.`); return; }
      setLines((l) => ({ ...l, [bc]: { ...piece, inBox: false, booked: false, buying: true } }));
      setCode('');
    } catch (e) { setErr(errText(e, 'Could not find that barcode')); }
    finally { setBusy(''); }
  };

  const save = async () => {
    setBusy('save'); setErr(null);
    try { await setCourierItems(row.appointment_id, buying.map((p) => p.zoddle_barcode)); onSaved(); }
    catch (e) { setErr(errText(e, 'Could not save the bill')); }
    finally { setBusy(''); }
  };

  if (!lines) return <BillSkeleton />;

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto -mx-1 px-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-gray-200">
              <th className="text-left font-medium py-2 w-8">#</th>
              <th className="text-left font-medium py-2">Item</th>
              <th className="text-left font-medium py-2">Size</th>
              <th className="text-left font-medium py-2">Source</th>
              <th className="text-right font-medium py-2">Amount</th>
              {!locked && <th className="w-10"><span className="sr-only">Remove</span></th>}
            </tr>
          </thead>
          <tbody>
            {buying.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-gray-500 text-sm">No pieces on the bill. Add a barcode below, or mark the order RTO if nothing is kept.</td></tr>
            )}
            {buying.map((p, i) => (
              <tr key={p.zoddle_barcode} className="border-b border-gray-100 last:border-0">
                <td className="py-2.5 text-gray-500 tabular-nums align-middle">{i + 1}</td>
                <td className="py-2.5">
                  <div className="flex items-center gap-3">
                    <Thumb url={p.image_url} />
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate max-w-[22rem]">{p.design_name || p.zoddle_barcode}</p>
                      <p className="text-xs text-gray-500 flex flex-wrap gap-x-2">
                        {p.brand && <span>{p.brand}</span>}
                        <span className="tabular-nums">{p.zoddle_barcode}</span>
                      </p>
                    </div>
                  </div>
                </td>
                <td className="py-2.5 text-gray-700">{p.size || '—'}</td>
                <td className="py-2.5">
                  <div className="flex flex-wrap gap-1">
                    <Tag>{p.booked ? 'Booked' : 'Added'}</Tag>
                    {p.inBox ? <Tag tone="green">In box</Tag> : <Tag tone="amber">Not in box, sent later</Tag>}
                  </div>
                </td>
                <td className="py-2.5 text-right tabular-nums text-gray-900">{rupees(p.price)}</td>
                {!locked && (
                  <td className="py-2.5 text-right">
                    <button onClick={() => toggle(p.zoddle_barcode)} title="Remove from bill" aria-label={`Remove ${p.zoddle_barcode} from bill`}
                      className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-zoddle-pink hover:bg-pink-50 ${PRESS} ${FOCUS}`}>
                      <X size={16} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!locked && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Plus size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" aria-hidden="true" />
            <input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()}
              placeholder="Scan or type a barcode to add" aria-label="Barcode to add"
              className={`${INPUT} pl-8 w-72 max-w-full`} />
          </div>
          <button onClick={add} disabled={!!busy || !code.trim()} className={BTN_SECONDARY}>
            {busy === 'add' ? 'Finding…' : 'Add to bill'}
          </button>
        </div>
      )}

      {coming.length > 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/60 px-3 py-3">
          <p className="text-xs font-semibold text-gray-700 mb-2">
            Coming back ({coming.length}): not on the bill; reversed when the parcel returns
          </p>
          <div className="flex flex-wrap gap-2">
            {coming.map((p) => (
              <div key={p.zoddle_barcode} className="flex items-center gap-2 bg-white rounded-lg ring-1 ring-gray-200 pl-1.5 pr-2 py-1.5">
                <Thumb url={p.image_url} size="w-9 h-9" />
                <div className="leading-tight">
                  <p className="text-xs text-gray-800 tabular-nums">{p.zoddle_barcode}</p>
                  <p className="text-[11px] text-gray-500">{p.size || ''}{p.inBox ? (p.size ? ', in box' : 'In box') : ''}</p>
                </div>
                {!locked && (
                  <button onClick={() => toggle(p.zoddle_barcode)}
                    className={`ml-1 inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900 rounded px-1 py-0.5 ${FOCUS}`}>
                    <Undo2 size={13} aria-hidden="true" />Add back
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {err && <p role="alert" className="text-sm text-zoddle-pink font-medium">{err}</p>}

      <div className="flex flex-wrap items-center gap-3 rounded-lg bg-gray-50 px-3 py-2.5">
        {!locked && (
          <>
            <button onClick={save} disabled={!!busy || !buying.length || (!dirty && !!order)} className={BTN_PRIMARY}>
              {busy === 'save' ? 'Saving…' : order ? 'Save changes' : 'Confirm bill'}
            </button>
            {dirty && order && <span className="text-xs text-amber-800">Unsaved changes: save the bill before taking payment.</span>}
          </>
        )}
        <div className="ml-auto flex items-baseline gap-3">
          <span className="text-xs text-gray-600">Total ({buying.length} piece{buying.length !== 1 ? 's' : ''})</span>
          <span className="text-lg font-semibold text-gray-900 tabular-nums">{rupees(total)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Step 2: payment ─────────────────────────────────────────────────────────
function Payment({ row, disabled, run, busy, notice }) {
  const order = row.order;
  const [mode, setMode] = useState('app');
  const [utr, setUtr] = useState('');
  const seg = (key, label) => (
    <button onClick={() => setMode(key)} aria-pressed={mode === key}
      className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${FOCUS} ${mode === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
      {label}
    </button>
  );
  return (
    <div className={`space-y-4 ${disabled ? 'opacity-50 pointer-events-none' : ''}`} aria-disabled={disabled}>
      <div className="inline-flex rounded-lg bg-gray-100 p-1">{seg('app', 'Skipzy app')}{seg('upi', 'UPI transfer')}</div>
      {mode === 'app' ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 max-w-prose">
            The customer's <b className="font-semibold text-gray-800">Scan &amp; Pay</b> becomes <b className="font-semibold text-gray-800">Pay now</b> in their Skipzy app for {rupees(order?.amount)}. The order closes by itself when they pay.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => run('app', () => requestAppPayment(row.appointment_id), 'Sent to the Skipzy app. The customer now sees Pay now.')}
              disabled={!!busy}
              className={`inline-flex items-center gap-1.5 bg-sky-600 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-sky-700 disabled:opacity-50 ${PRESS} ${FOCUS}`}>
              {busy === 'app' ? 'Sending…' : order?.payment_status === 'PENDING' ? 'Resend to app' : 'Request payment in app'}
            </button>
            {order?.payment_status === 'PENDING' && (
              <span className="inline-flex items-center gap-1.5 text-xs text-sky-800">
                <span className="w-1.5 h-1.5 rounded-full bg-zoddle-blue animate-pulse motion-reduce:animate-none" aria-hidden="true" />
                Waiting for the customer to pay
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-start gap-5">
            <QRCodeSVG value={upiLink(order?.amount)} size={152} marginSize={2} title="Skipzy UPI QR" className="rounded-lg bg-white ring-1 ring-gray-200" />
            <div className="space-y-1.5 max-w-md">
              <p className="text-sm font-semibold text-gray-900">Ask the customer to pay {rupees(order?.amount)}</p>
              <p className="text-sm text-gray-600">
                Send them this QR (Skipzy's own UPI; the amount is already filled in). When their payment screenshot arrives on WhatsApp, check the amount, then enter the UTR below. Your name is recorded with it.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="UTR / reference number" aria-label="UTR / reference number"
              className={`${INPUT} w-64 max-w-full tabular-nums`} />
            <button onClick={() => run('paid', () => markCourierPaid(row.appointment_id, utr),
              (o) => (o.sheet_written ? 'Marked paid and added to the orders sheet.' : 'Marked paid. NOT added to the orders sheet: add the row by hand.'))}
              disabled={!!busy || !utr.trim()} className={BTN_PRIMARY}>
              {busy === 'paid' ? 'Saving…' : 'Mark as paid'}
            </button>
          </div>
        </div>
      )}
      <Notice msg={notice} />
    </div>
  );
}

// ── One order ───────────────────────────────────────────────────────────────
const STEP_OF_ACTION = { bill: 'bill', app: 'payment', paid: 'payment', invoice: 'invoice', rto: 'rto' };

// One booking in the list. Clicking it opens that booking on its own (CourierDetail).
function OrderRow({ row, onOpen }) {
  const order = row.order;
  const stage = stageOf(order);
  // Photos for the row: what's on the bill once there is one, else what's in the box.
  const pics = (order?.items?.length ? order.items : row.in_box) || [];
  const pieceLabel = order
    ? `${(order.items || []).length} on bill`
    : `${row.booked.length} booked`;

  return (
    <button onClick={onOpen}
      className={`group w-full flex items-center gap-4 px-4 py-3.5 text-left transition-colors hover:bg-gray-50 ${FOCUS} focus-visible:ring-inset`}>
      <span className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ${tintFor(row.customer_name || row.uid)}`} aria-hidden="true">
        {initials(row.customer_name)}
      </span>

      <div className={`min-w-0 flex-1 ${ROW_GRID}`}>
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 truncate">{row.customer_name || '—'}</p>
          <p className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-gray-500 tabular-nums">
            <span className="font-medium text-gray-700">{row.uid}</span>
            <span className="inline-flex items-center gap-1"><Phone size={12} aria-hidden="true" />{row.phone || '—'}</span>
          </p>
        </div>

        {/* On phones these three share one line under the name; from sm up they are grid columns. */}
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 sm:mt-0 sm:contents">
          <div className="flex items-center gap-2.5">
            <div className="flex -space-x-2">
              {pics.slice(0, 3).map((p) => (
                p.image_url
                  ? <img key={p.zoddle_barcode} src={cldThumb(p.image_url, 120)} alt="" className="w-7 h-7 rounded-md object-cover ring-2 ring-white" />
                  : <span key={p.zoddle_barcode} className="w-7 h-7 rounded-md bg-gray-100 ring-2 ring-white" />
              ))}
              {pics.length === 0 && <span className="w-7 h-7 rounded-md bg-gray-100 ring-2 ring-white flex items-center justify-center"><PackageOpen size={13} className="text-gray-400" aria-hidden="true" /></span>}
            </div>
            <span className="text-sm text-gray-700 whitespace-nowrap">
              {pieceLabel}{pics.length > 3 && <span className="text-gray-500"> (+{pics.length - 3})</span>}
            </span>
          </div>

          <div className="text-sm leading-tight" title={`Dispatched ${when(row.dispatched_at)}`}>
            <p className="inline-flex items-center gap-1.5 text-gray-700 whitespace-nowrap"><Truck size={13} className="text-gray-400" aria-hidden="true" />{ago(row.dispatched_at)}</p>
            <p className="hidden sm:block text-xs text-gray-500 mt-0.5">{when(row.dispatched_at)}</p>
          </div>

          <div><StatusChip stage={stage} /></div>
        </div>
      </div>

      <div className="w-24 sm:w-28 text-right shrink-0">
        {order
          ? <span className="text-base font-semibold text-gray-900 tabular-nums whitespace-nowrap">{rupees(order.amount)}</span>
          : <span className="text-sm text-gray-500">Not billed</span>}
      </div>
      <ChevronRight size={18} aria-hidden="true"
        className="text-gray-400 shrink-0 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-gray-600 motion-reduce:transition-none" />
    </button>
  );
}

// One booking opened on its own: header, then the bill → payment → invoice steps.
function CourierDetail({ row, onChanged, onBack }) {
  const order = row.order;
  const stage = stageOf(order);
  const paid = stage === 'paid';
  const [billOpen, setBillOpen] = useState(!order);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [rto, setRto] = useState(false);
  const [rtoReason, setRtoReason] = useState('');
  const onDirty = useCallback((d) => setDirty(d), []);

  const run = async (key, fn, ok) => {
    setBusy(key); setMsg(null);
    try {
      const out = await fn();
      setMsg({ kind: 'ok', text: typeof ok === 'function' ? ok(out) : ok, at: STEP_OF_ACTION[key] });
      onChanged();
      return out;
    } catch (e) {
      setMsg({ kind: 'err', text: errText(e, 'Something went wrong'), at: STEP_OF_ACTION[key] });
      return null;
    } finally { setBusy(''); }
  };
  const noticeFor = (step) => (msg?.at === step ? msg : null);

  const invoice = () => run('invoice', async () => {
    const r = await generateReceipt(row.uid);
    downloadHtml(r.html, r.filename);
    return r;
  }, (r) => `Invoice ${r.invoice_number} downloaded`);

  const billDone = !!order && !dirty;
  const billState = billDone ? 'done' : 'current';
  const payState = paid ? 'done' : order ? 'current' : 'upcoming';
  const invState = paid ? 'current' : 'upcoming';
  const showBill = billOpen || !billDone;

  return (
    <div className="space-y-4 animate-reveal motion-reduce:animate-none">
      <button onClick={onBack}
        className={`inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 rounded px-1 -ml-1 ${FOCUS}`}>
        <ArrowLeft size={16} aria-hidden="true" />All bookings
      </button>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <header className="flex flex-wrap items-center gap-x-4 gap-y-3 px-5 py-4 border-b border-gray-100">
          <span className={`w-11 h-11 shrink-0 rounded-full flex items-center justify-center text-sm font-semibold ${tintFor(row.customer_name || row.uid)}`} aria-hidden="true">
            {initials(row.customer_name)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h3 className="text-lg font-semibold text-gray-900 truncate">{row.customer_name || '—'}</h3>
              <StatusChip stage={stage} />
            </div>
            <p className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-gray-600 tabular-nums">
              <span className="font-medium text-gray-800">{row.uid}</span>
              <span className="inline-flex items-center gap-1.5"><Phone size={13} className="text-gray-400" aria-hidden="true" />{row.phone || '—'}</span>
              <span className="inline-flex items-center gap-1.5"><Truck size={13} className="text-gray-400" aria-hidden="true" />Dispatched {when(row.dispatched_at)}</span>
            </p>
          </div>
          <div className="text-right">
            {order
              ? <p className="text-xl font-semibold text-gray-900 tabular-nums">{rupees(order.amount)}</p>
              : <p className="text-sm text-gray-500">Not billed, {row.booked.length} booked</p>}
          </div>
        </header>

        <div className="px-5 pb-5 pt-5">
          <ol>
            <Step n={1} title="Bill" state={billState}
              meta={(
                <div className="flex items-center gap-3">
                  {paid && <span className="text-gray-500">Locked: paid</span>}
                  {billDone && (
                    <button onClick={() => setBillOpen(!billOpen)}
                      className={`text-xs font-medium text-sky-700 hover:text-sky-900 rounded px-1 ${FOCUS}`}>
                      {billOpen ? 'Hide bill' : paid ? 'View bill' : 'Edit bill'}
                    </button>
                  )}
                </div>
              )}>
              {billDone && !billOpen && (
                <p className="text-sm text-gray-700">
                  {(order.items || []).length} piece{(order.items || []).length !== 1 ? 's' : ''},{' '}
                  <span className="font-semibold text-gray-900 tabular-nums">{rupees(order.amount)}</span>
                </p>
              )}
              {/* Kept mounted while hidden so unsaved edits and the dirty flag survive collapsing. */}
              <div className={showBill ? '' : 'hidden'}>
                <Bill row={row} locked={paid} onDirty={onDirty}
                  onSaved={() => { setMsg({ kind: 'ok', text: 'Bill saved', at: 'bill' }); setBillOpen(false); onChanged(); }} />
              </div>
              {noticeFor('bill') && <div className="mt-2"><Notice msg={noticeFor('bill')} /></div>}
            </Step>

            <Step n={2} title="Payment" state={payState}
              meta={paid ? <span className="text-green-700">Received {when(order.paid_at)}</span> : null}>
              {paid ? (
                <p className="text-sm text-gray-700">
                  {rupees(order.amount)} received {order.payment_method === 'upi' ? `by UPI (ref ${order.payment_reference})` : 'in the Skipzy app'}.
                </p>
              ) : !order ? (
                <p className="text-sm text-gray-500">Confirm the bill first.</p>
              ) : (
                <Payment row={row} disabled={dirty} run={run} busy={busy} notice={noticeFor('payment')} />
              )}
              {paid && noticeFor('payment') && <div className="mt-2"><Notice msg={noticeFor('payment')} /></div>}
            </Step>

            <Step n={3} title="Invoice" state={invState} last>
              {paid ? (
                <div className="space-y-2">
                  <button onClick={invoice} disabled={!!busy} className={BTN_SECONDARY}>
                    <FileDown size={16} aria-hidden="true" />
                    {busy === 'invoice' ? 'Preparing…' : 'Download tax invoice'}
                  </button>
                  <Notice msg={noticeFor('invoice')} />
                </div>
              ) : <p className="text-sm text-gray-500">Available once payment is received.</p>}
            </Step>
          </ol>

          {!paid && (
            <div className="mt-5 flex flex-wrap items-center gap-3 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-600">Customer refused or didn't take the parcel?</p>
              {rto ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input value={rtoReason} onChange={(e) => setRtoReason(e.target.value)} placeholder="Reason (optional)" aria-label="RTO reason (optional)"
                    className={`${INPUT} py-1.5 text-xs w-52`} />
                  <button onClick={() => run('rto', () => markCourierRto(row.appointment_id, rtoReason || undefined),
                    (o) => (o?.rto?.sheet_written === false
                      ? 'Marked RTO. NOT added to the RTO_DOL sheet: add the row by hand. Reverse the box when it is back.'
                      : o?.rto?.sheet_written == null
                        ? 'Already marked RTO. Reverse the box when it is back.'
                        : 'Marked RTO and added to the RTO_DOL sheet. Reverse the box when it is back.'))}
                    disabled={!!busy}
                    className={`bg-zoddle-pink text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:brightness-95 disabled:opacity-50 ${PRESS} ${FOCUS}`}>
                    {busy === 'rto' ? 'Saving…' : 'Confirm RTO'}
                  </button>
                  <button onClick={() => setRto(false)} className={`text-xs text-gray-600 hover:text-gray-900 rounded px-1 ${FOCUS}`}>Cancel</button>
                </div>
              ) : (
                <button onClick={() => setRto(true)}
                  className={`text-xs font-medium text-pink-700 border border-zoddle-pink/50 px-3 py-1.5 rounded-lg hover:bg-pink-50 ${PRESS} ${FOCUS}`}>
                  Mark RTO
                </button>
              )}
              {noticeFor('rto') && <div className="w-full"><Notice msg={noticeFor('rto')} /></div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 animate-pulse motion-reduce:animate-none" aria-label="Loading courier orders">
      {[0, 1, 2].map((i) => (
        <div key={i} className="px-4 py-3.5 flex items-center gap-4">
          <div className="w-9 h-9 rounded-full bg-gray-100" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-40 rounded bg-gray-100" />
            <div className="h-3 w-72 max-w-full rounded bg-gray-100" />
          </div>
          <div className="h-4 w-20 rounded bg-gray-100" />
        </div>
      ))}
    </div>
  );
}

// ── The tab ─────────────────────────────────────────────────────────────────
const LIST_PATH = '/order-ops/courier';
const uidFromUrl = () => {
  const { pathname } = window.location;
  if (!pathname.startsWith(`${LIST_PATH}/`)) return null;
  const rest = pathname.slice(LIST_PATH.length + 1).split('/')[0];
  return rest ? decodeURIComponent(rest) : null;
};

export default function CatalogCourierTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  // The booking opened on its own lives in the URL: /order-ops/courier/<booking ID>.
  // Reload, Back/Forward and shared links all land on the same booking.
  const [openUid, setOpenUid] = useState(uidFromUrl);
  const pushedRef = useRef(false);   // true when we opened it from the list, so "All bookings" can just go Back
  useEffect(() => {
    const onPop = () => { pushedRef.current = false; setOpenUid(uidFromUrl()); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const openBooking = (uid) => {
    window.history.pushState(null, '', `${LIST_PATH}/${encodeURIComponent(uid)}`);
    pushedRef.current = true;
    setOpenUid(uid);
  };
  const closeBooking = () => {
    if (pushedRef.current) { window.history.back(); return; }   // popstate clears openUid
    window.history.pushState(null, '', LIST_PATH);
    setOpenUid(null);
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await listCourierOrders()); }
    catch (e) { setError(errText(e, 'Failed to load courier orders')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const orders = data?.orders || [];
  const counts = orders.reduce((c, r) => { const s = stageOf(r.order); c[s] = (c[s] || 0) + 1; return c; }, {});
  const needle = q.trim().toLowerCase();
  const shown = orders.filter((r) => (filter === 'all' || stageOf(r.order) === filter)
    && (!needle || [r.uid, r.customer_name, r.phone].some((v) => (v || '').toLowerCase().includes(needle))));

  const tab = (key, label) => {
    const active = filter === key;
    const n = key === 'all' ? orders.length : (counts[key] || 0);
    return (
      <button key={key} onClick={() => setFilter(key)} aria-pressed={active}
        className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${FOCUS} ${active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
        {key !== 'all' && <span className={`w-1.5 h-1.5 rounded-full ${STAGES[key].dot}`} aria-hidden="true" />}
        {label}
        <span className={`text-xs tabular-nums ${active ? 'text-gray-700' : 'text-gray-500'}`}>{n}</span>
      </button>
    );
  };

  // Looked up from the latest list, so a refresh after an action shows the new state.
  const openRow = openUid ? orders.find((r) => r.uid === openUid) : null;
  if (openRow) {
    return (
      <CourierDetail key={openRow.appointment_id} row={openRow} onChanged={load} onBack={closeBooking} />
    );
  }
  if (openUid) {
    // Still loading, or the booking isn't in this list any more (e.g. its parcel was reversed).
    return (
      <div className="space-y-4">
        <button onClick={closeBooking}
          className={`inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 rounded px-1 -ml-1 ${FOCUS}`}>
          <ArrowLeft size={16} aria-hidden="true" />All bookings
        </button>
        {!data ? <ListSkeleton /> : (
          <div className="flex flex-col items-center text-center py-16 px-4 bg-white border border-dashed border-gray-300 rounded-xl">
            <PackageOpen size={28} className="text-gray-400 mb-3" aria-hidden="true" />
            <p className="text-sm font-medium text-gray-700">Booking {openUid} isn't in this list.</p>
            <p className="mt-1 text-sm text-gray-500">It may already be closed, or the booking ID in the link is wrong.</p>
          </div>
        )}
        {error && <p role="alert" className="text-sm text-zoddle-pink font-medium">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex max-w-full overflow-x-auto rounded-lg bg-gray-100 p-1">
          {tab('all', 'All')}{tab('new', 'Needs bill')}{tab('billed', 'Bill ready')}{tab('awaiting', 'Awaiting payment')}{tab('paid', 'Paid')}
        </div>
        <div className="ml-auto flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" aria-hidden="true" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order ID, name or phone" aria-label="Search order ID, name or phone"
              className={`${INPUT} pl-9 w-full sm:w-72`} />
          </div>
          <button onClick={load} disabled={loading} title="Refresh" aria-label="Refresh"
            className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-900 ${PRESS} ${FOCUS}`}>
            <RotateCw size={16} className={loading ? 'animate-spin motion-reduce:animate-none' : ''} />
          </button>
        </div>
      </div>

      {data?.rider_lookup_failed && (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Couldn't reach the rider service, so every dispatched catalog order is listed (some may have a rider).
        </p>
      )}
      {error && <p role="alert" className="text-sm text-zoddle-pink font-medium">{error}</p>}

      {!data && loading && <ListSkeleton />}

      {data && !shown.length && (
        <div className="flex flex-col items-center text-center py-16 px-4 bg-white border border-dashed border-gray-300 rounded-xl">
          <PackageOpen size={28} className="text-gray-400 mb-3" aria-hidden="true" />
          <p className="text-sm font-medium text-gray-700">{orders.length ? 'No orders match' : 'No catalog orders waiting on a courier'}</p>
          {orders.length > 0 && (filter !== 'all' || needle) && (
            <button onClick={() => { setFilter('all'); setQ(''); }}
              className={`mt-3 text-sm font-medium text-sky-700 hover:text-sky-900 rounded px-1 ${FOCUS}`}>
              Clear search and filters
            </button>
          )}
        </div>
      )}

      {shown.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="hidden sm:flex items-center gap-4 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500">
            <span className="w-9 shrink-0" aria-hidden="true" />
            <div className={`flex-1 ${ROW_GRID}`}>
              <span>Customer</span><span>Pieces</span><span>Dispatched</span><span>Status</span>
            </div>
            <span className="w-28 text-right shrink-0">Amount</span>
            <span className="w-[18px] shrink-0" aria-hidden="true" />
          </div>
          <div className="divide-y divide-gray-100">
            {shown.map((row) => <OrderRow key={row.appointment_id} row={row} onOpen={() => openBooking(row.uid)} />)}
          </div>
        </div>
      )}
    </div>
  );
}
